import { Node, parseTree } from 'jsonc-parser';

export type IdAnnotation = {
  row: number;
  column: number;
  id: string;
};

/**
 * Find ID annotations using jsonc-parser, which tolerates invalid JSON.
 * This allows annotations to appear even while the user is actively editing.
 */
export const findIdAnnotations = (jsonText: string): IdAnnotation[] => {
  const tree = parseTree(jsonText, undefined, {
    disallowComments: false,
    allowTrailingComma: true,
    allowEmptyContent: true,
  });

  if (!tree) return [];

  const annotations: IdAnnotation[] = [];
  const lines = jsonText.split('\n');

  const offsetToPosition = (
    offset: number
  ): { line: number; column: number } => {
    let remaining = offset;
    let lineIndex = 0;

    while (lineIndex < lines.length) {
      const lineLength = (lines[lineIndex]?.length ?? 0) + 1;
      if (remaining < lineLength) {
        return { line: lineIndex, column: remaining };
      }
      remaining -= lineLength;
      lineIndex += 1;
    }

    return { line: lines.length - 1, column: 0 };
  };

  const visitNode = (node: Node) => {
    if (node.type === 'object' && node.children) {
      let idValue: string | undefined;
      let categoryValue: string | undefined;

      node.children.forEach(property => {
        if (property.type === 'property' && property.children?.length === 2) {
          const [keyNode, valueNode] = property.children;
          if (keyNode?.type === 'string' && valueNode?.type === 'string') {
            if (keyNode.value === 'id') {
              idValue = valueNode.value;
            } else if (keyNode.value === 'category') {
              categoryValue = valueNode.value;
            }
          }
        }
      });

      if (idValue && categoryValue !== 'tuple') {
        const pos = offsetToPosition(node.offset);
        annotations.push({
          row: pos.line,
          column: pos.column,
          id: idValue,
        });
      }

      node.children.forEach(child => visitNode(child));
    } else if (node.type === 'array' && node.children) {
      node.children.forEach(child => visitNode(child));
    } else if (node.type === 'property' && node.children) {
      node.children.forEach(child => visitNode(child));
    }
  };

  visitNode(tree);
  return annotations;
};
